'use client';

import { createContext, useContext, useEffect, useRef } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';

import { DatesProvider } from '@mantine/dates';

import { useAddTipNotification, useAppContext } from '@context/AppContext';
import { FrontNotificationsProvider } from '@context/FrontNotificationsContext';
import { useHateoasContext } from '@context/HateoasContext';
import { RealtimeProvider } from '@context/RealtimeContext';

import { getBoondParentID, initBoondIframeSdk } from '@utils/boond-iframe-sdk';
import { boondEntityIdentityCache } from '@utils/boondEntityIdentityCache';
import dayjs from '@utils/dayjs';

import { deleteIFrameCookies, fetchBootstrapApi, logoutAction } from '@/app/actions';

const MainContext = createContext();
const BOOTSTRAP_CACHE_KEY = 'main-provider.bootstrap-cache.v2';
const BOOTSTRAP_CACHE_TTL_MS = 30 * 60 * 1000;

const readCachedBootstrap = () => {
    try {
        const raw = sessionStorage.getItem(BOOTSTRAP_CACHE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        const isExpired = !parsed?.fetchedAt || Date.now() - parsed.fetchedAt > BOOTSTRAP_CACHE_TTL_MS;
        if (isExpired) {
            sessionStorage.removeItem(BOOTSTRAP_CACHE_KEY);
            return null;
        }

        return parsed.data || null;
    } catch {
        sessionStorage.removeItem(BOOTSTRAP_CACHE_KEY);
        return null;
    }
};

const writeCachedBootstrap = (data) => {
    try {
        sessionStorage.setItem(BOOTSTRAP_CACHE_KEY, JSON.stringify({
            fetchedAt: Date.now(),
            data,
        }));
    } catch {
        // Ignore sessionStorage quota and serialization errors.
    }
};

export default function MainProvider({ children }) {
    const locale = useLocale();
    const [user] = useAppContext('user');
    const [, setIframe] = useAppContext('iframe');
    const [, setInitialize] = useAppContext('initialize');
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const isIntegrated = searchParams.get('screenType') === 'integrated';
    const addTip = useAddTipNotification();
    const { setScope } = useHateoasContext();
    const boondSdkInitRef = useRef(null);
    const boondAutoResizeRef = useRef(false);

    const hydrateBootstrap = async (preferCache = true) => {
        const cachedBootstrap = preferCache ? readCachedBootstrap() : null;
        const { links, user, menu, bootstrapData } = cachedBootstrap || await fetchBootstrapApi();
        if (!cachedBootstrap) {
            writeCachedBootstrap({ links, user, menu, bootstrapData });
        }

        setScope('app', bootstrapData, { sourceEndpoint: '/api' });

        if (user && !user.hasLicense) {
            addTip('USER_NO_LICENSE');
        }
        setInitialize({ user, menu });
    };

    useEffect(() => {
        const initializeFrame = async () => {
            if (window.self !== window.top) {
                setIframe(true);
                if (!isIntegrated) {
                    document.body.style.overflow = 'hidden';
                }
            } else {
                await deleteIFrameCookies();
            }
        };

        initializeFrame();
    }, []);

    useEffect(() => {
        if (!pathname || pathname.startsWith('/login') || user?._id) {
            return;
        }

        hydrateBootstrap(false);
    }, [pathname, user?._id]);

    useEffect(() => {
        window.addEventListener('UNAUTHORIZED', logout);

        return () => {
            window.removeEventListener('UNAUTHORIZED', logout);
        };
    }, []);

    useEffect(() => {
        if (window.self !== window.top) {
            const observer = new MutationObserver(resize);

            observer.observe(document.getElementById('main'), {
                childList: true,
                subtree: true,
            });
            resize();

            return () => {
                observer.disconnect();
            };
        }
    }, []);

    useEffect(() => {
        // Configuration de la langue
        dayjs.locale(locale);
    }, [locale]);

    const resize = () => {
        const boondManager = window.BoondManager;
        if (!boondManager) {
            return;
        }
        const parentID = getBoondParentID(document.cookie);

        if (!boondSdkInitRef.current) {
            boondSdkInitRef.current = initBoondIframeSdk(boondManager, parentID);
        }

        boondSdkInitRef.current.then(() => {
            if (!boondAutoResizeRef.current) {
                boondAutoResizeRef.current = true;
                boondManager.setAutoResize();
                return;
            }
            boondManager.setSize();
        });
    };

    const logout = async () => {
        sessionStorage.removeItem(BOOTSTRAP_CACHE_KEY);
        await boondEntityIdentityCache.clear();
        await logoutAction();
        router.push('/login');
        router.refresh();
    };

    return (
        <MainContext.Provider value={{ logout }}>
            <DatesProvider settings={{ locale }}>
                <RealtimeProvider>
                    <FrontNotificationsProvider>
                        {children}
                    </FrontNotificationsProvider>
                </RealtimeProvider>
            </DatesProvider>
        </MainContext.Provider>
    );
}

export function useMainContext() {
    return useContext(MainContext);
}
